// src/pages/admin/CompanyHeadManagement.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaUserTie,
  FaUsers,
  FaBuilding,
  FaCheck,
  FaTimes,
  FaSave,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendar,
  FaUserCheck,
  FaImage
} from 'react-icons/fa';

const CompanyHeadManagement = () => {
  const [companyHeads, setCompanyHeads] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    profilePicture: "",
    username: "",
    email: "",
    phoneNumber: "",
    address: "",
    company: "",
    dateOfBirth: "",
    dateOfJoining: "",
    password: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedCompanyHead, setSelectedCompanyHead] = useState(null);

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5);

  useEffect(() => {
    fetchCompanyHeads();
    fetchCompanies();
  }, []);

  // ---------------------------------------------------------------------
  // Fetch All Company Heads (active only)
  // ---------------------------------------------------------------------
  const fetchCompanyHeads = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/company-heads`
      );
      // Returns only isActive = true from the backend
      setCompanyHeads(response.data);
    } catch (error) {
      console.error("Error fetching company heads:", error);
      Swal.fire("Error", "Failed to fetch company heads", "error");
    }
  };

  // ---------------------------------------------------------------------
  // Fetch All Companies
  // ---------------------------------------------------------------------
  const fetchCompanies = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/companies`
      );
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
      Swal.fire("Error", "Failed to fetch companies", "error");
    }
  };

  // ---------------------------------------------------------------------
  // Form Handlers
  // ---------------------------------------------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({ ...prevForm, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    setForm((prevForm) => ({ ...prevForm, phoneNumber: e.target.value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setForm((prevForm) => ({ ...prevForm, profilePicture: file }));
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  // ---------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------
  const validateForm = () => {
    const requiredFields = ["username", "email", "phoneNumber", "address", "company"];
    for (let field of requiredFields) {
      if (!form[field]?.trim()) {
        Swal.fire("Warning", `${field} is required`, "warning");
        return false;
      }
    }
    // Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      Swal.fire("Warning", "Invalid email format", "warning");
      return false;
    }
    // Phone
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(form.phoneNumber)) {
      Swal.fire("Warning", "Invalid phone number format", "warning");
      return false;
    }
    return true;
  };

  // ---------------------------------------------------------------------
  // (Optional) Check existing account
  // ---------------------------------------------------------------------
  const checkExistingAccount = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/check-account`,
        { email: form.email }
      );
      return response.data.exists;
    } catch (error) {
      console.error("Error checking existing account:", error);
      Swal.fire("Error", "Failed to check for existing account", "error");
      return false;
    }
  };

  // ---------------------------------------------------------------------
  // Add or Update
  // ---------------------------------------------------------------------
  const handleAddOrUpdateCompanyHead = async () => {
    if (!validateForm()) return;

    // Enforce the "account must exist" rule if desired
    if (!isEditing) {
      const exists = await checkExistingAccount();
      if (exists) {
        Swal.fire(
          "Warning",
          "Company Head must not be associated with an existing account (any role)",
          "warning"
        );
        return;
      }
    }

    try {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        formData.append(key, form[key]);
      });

      if (isEditing) {
        // Update
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/company-heads/${editingId}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        Swal.fire("Success", "Company head updated successfully", "success");
      } else {
        // Create
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/company-heads`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.fire("Success", "Company head added successfully", "success");
      }

      fetchCompanyHeads();
      resetForm();
      closeFormModal();
    } catch (error) {
      console.error("Error saving company head:", error);
      Swal.fire("Error", "Failed to save company head", "error");
    }
  };

  // ---------------------------------------------------------------------
  // Start Editing
  // ---------------------------------------------------------------------
  const startEditing = async (companyHead) => {
    const convertUrlToFile = async (url) => {
      const response = await fetch(url);
      const data = await response.blob();
      return new File([data], 'filename', { type: data.type });
    };
    setIsEditing(true);
    setEditingId(companyHead._id);
    setForm({
      profilePicture: await convertUrlToFile(`${process.env.REACT_APP_API_BASE_URL}/company-heads/profilePicture/${companyHead._id}`),
      username: companyHead.username,
      email: companyHead.email,
      phoneNumber: companyHead.phoneNumber,
      address: companyHead.address,
      company: companyHead.company?._id || "",
      dateOfBirth: companyHead.dateOfBirth
        ? new Date(companyHead.dateOfBirth).toISOString().split("T")[0]
        : "",
      dateOfJoining: companyHead.dateOfJoining
        ? new Date(companyHead.dateOfJoining).toISOString().split("T")[0]
        : "",
      password: "",
    });

    // If stored as a string URL, handle accordingly
    if (typeof companyHead.profilePicture === "string") {
      setImagePreview(`${process.env.REACT_APP_API_BASE_URL}/${companyHead.profilePicture}`);
    } else {
      setImagePreview(null);
    }

    openFormModal();
  };

  // ---------------------------------------------------------------------
  // Deactivate
  // ---------------------------------------------------------------------
  const handleDeactivate = async (id) => {
    const confirmResult = await Swal.fire({
      title: "Confirm Deactivate",
      text: "Are you sure you want to deactivate this company head?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, deactivate",
      cancelButtonText: "Cancel",
    });

    if (confirmResult.isConfirmed) {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/company-heads/deactivate/${id}`
        );
        Swal.fire("Deactivated!", "Company head deactivated successfully.", "success");
        fetchCompanyHeads();
      } catch (error) {
        console.error("Error deactivating company head:", error);
        Swal.fire("Error", "Failed to deactivate company head", "error");
      }
    }
  };

  // ---------------------------------------------------------------------
  // View Details
  // ---------------------------------------------------------------------
  const handleViewDetails = (id) => {
    fetchCompanyHeadById(id);
  };

  const fetchCompanyHeadById = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/company-heads/${id}`
      );
      setSelectedCompanyHead(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Error fetching company head:", error);
      Swal.fire("Error", "Failed to fetch company head details", "error");
    }
  };

  // ---------------------------------------------------------------------
  // Reset Form
  // ---------------------------------------------------------------------
  const resetForm = () => {
    setForm({
      profilePicture: "",
      username: "",
      email: "",
      phoneNumber: "",
      address: "",
      company: "",
      dateOfBirth: "",
      dateOfJoining: "",
      password: "",
    });
    setIsEditing(false);
    setEditingId(null);
    setImagePreview(null);
  };

  // ---------------------------------------------------------------------
  // Modal Control
  // ---------------------------------------------------------------------
  const openFormModal = () => setShowFormModal(true);
  const closeFormModal = () => {
    setShowFormModal(false);
    resetForm();
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedCompanyHead(null);
  };

  // ---------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------
  const totalRecords = companyHeads.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedHeads = companyHeads.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
              <FaUserTie className="text-blue-600 mr-3 text-2xl" />
              Company Head Management
            </h1>
            <p className="text-gray-600 mt-1 text-sm">Manage company heads and their organizational roles</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              openFormModal();
            }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl hover:from-blue-700 hover:to-purple-700 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            <FaPlus className="w-4 h-4" />
            <span className="font-semibold text-sm">Add New Company Head</span>
          </button>
        </div>
      </div>

      {/* Table of Active Company Heads */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th
                  scope="col"
                  className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                >
                  <div className="flex items-center space-x-2">
                    <FaUserTie className="w-4 h-4" />
                    <span>Company Head</span>
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                >
                  <div className="flex items-center space-x-2">
                    <FaEnvelope className="w-4 h-4" />
                    <span>Contact Info</span>
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                >
                  <div className="flex items-center space-x-2">
                    <FaBuilding className="w-4 h-4" />
                    <span>Company</span>
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {paginatedHeads.map((companyHead, index) => (
              <tr key={companyHead._id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 group">
                <td className="px-8 py-6 whitespace-nowrap">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/company-heads/profilePicture/${companyHead._id}`}
                        alt="Profile"
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm hidden">
                        {companyHead.username?.charAt(0)?.toUpperCase()}
                      </div>
                    </div>
                    <div>
                      <div className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                        {companyHead.username}
                      </div>
                      <div className="text-sm text-gray-500">Company Head #{index + 1}</div>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-6 whitespace-nowrap">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-sm">
                      <FaEnvelope className="w-3 h-3 text-gray-400" />
                      <span className="text-gray-900">{companyHead.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <FaPhone className="w-3 h-3 text-gray-400" />
                      <span className="text-gray-600">{companyHead.phoneNumber}</span>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-6 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <FaBuilding className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-base font-semibold text-gray-900">
                        {companyHead.company?.name || "No Company"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-6 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleViewDetails(companyHead._id)}
                      className="bg-blue-100 text-blue-600 hover:bg-blue-200 p-2 rounded-lg transition-all duration-200 hover:scale-110"
                      title="View Details"
                    >
                      <FaEye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => startEditing(companyHead)}
                      className="bg-green-100 text-green-600 hover:bg-green-200 p-2 rounded-lg transition-all duration-200 hover:scale-110"
                      title="Edit Company Head"
                    >
                      <FaEdit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeactivate(companyHead._id)}
                      className="bg-red-100 text-red-600 hover:bg-red-200 p-2 rounded-lg transition-all duration-200 hover:scale-110"
                      title="Deactivate"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {paginatedHeads.length === 0 && (
              <tr>
                <td
                  colSpan="4"
                  className="px-8 py-16 text-center"
                >
                  <div className="flex flex-col items-center">
                    <FaUserTie className="w-16 h-16 text-gray-300 mb-4" />
                    <span className="text-gray-500 text-sm font-medium">No company heads found</span>
                    <span className="text-gray-400 text-sm mt-2">Start by adding your first company head</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 flex items-center justify-between border-t border-gray-200">
            <div className="bg-white px-4 py-2 rounded-lg shadow-md">
              <p className="text-sm text-gray-700 font-medium">
                Page <span className="font-bold text-blue-600">{currentPage}</span> of{' '}
                <span className="font-bold text-blue-600">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-xl shadow-lg overflow-hidden">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-6 py-3 border border-gray-300 bg-white text-sm font-semibold text-gray-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50 transition-all duration-200"
                >
                  Previous
                </button>
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-6 py-3 border border-gray-300 bg-white text-sm font-semibold text-gray-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50 transition-all duration-200"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-4xl mx-4 rounded-2xl shadow-2xl p-4 relative border border-gray-200 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
                  <FaUserTie className="text-blue-600 mr-2 text-lg" />
                  {isEditing ? "Edit Company Head" : "Add New Company Head"}
                </h3>
                <p className="text-gray-600 mt-1 text-xs">
                  {isEditing ? "Update company head information" : "Create a new company head profile"}
                </p>
              </div>
              <button
                onClick={closeFormModal}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-all duration-200"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* username */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaUserTie className="w-3 h-3 text-blue-600" />
                    <span>Username</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={handleInputChange}
                  placeholder="Enter username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* email */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaEnvelope className="w-3 h-3 text-green-600" />
                    <span>Email</span>
                  </div>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* password */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaTimes className="w-3 h-3 text-red-600" />
                    <span>Password</span>
                  </div>
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleInputChange}
                  placeholder="Enter secure password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* phoneNumber */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaPhone className="w-3 h-3 text-purple-600" />
                    <span>Phone Number</span>
                  </div>
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* address */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaMapMarkerAlt className="w-3 h-3 text-orange-600" />
                    <span>Address</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleInputChange}
                  placeholder="Enter full address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* company */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaBuilding className="w-3 h-3 text-indigo-600" />
                    <span>Company</span>
                  </div>
                </label>
                <select
                  name="company"
                  value={form.company}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <option value="">Select Company</option>
                  {companies.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* dateOfBirth */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaCalendar className="w-3 h-3 text-pink-600" />
                    <span>Date of Birth</span>
                  </div>
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={form.dateOfBirth}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* dateOfJoining */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaCalendar className="w-3 h-3 text-teal-600" />
                    <span>Date of Joining</span>
                  </div>
                </label>
                <input
                  type="date"
                  name="dateOfJoining"
                  value={form.dateOfJoining}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* profilePicture */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaImage className="w-3 h-3 text-cyan-600" />
                    <span>Profile Picture</span>
                  </div>
                </label>
                <input
                  type="file"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {imagePreview && (
                  <div className="mt-2 flex justify-center">
                    <img
                      src={imagePreview}
                      alt="Profile Preview"
                      className="w-12 h-12 rounded-full object-cover border-2 border-blue-200 shadow-sm"
                    />
                  </div>
                )}
              </div>
            </div>
            {/* Modal Action Buttons */}
            <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200 mt-3">
              <button
                onClick={closeFormModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 font-semibold text-sm transition-all duration-200 flex items-center space-x-1"
              >
                <FaTimes className="w-3 h-3" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleAddOrUpdateCompanyHead}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center space-x-1 font-semibold text-sm shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                <FaSave className="w-3 h-3" />
                <span>{isEditing ? "Update" : "Create"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedCompanyHead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-3xl mx-4 rounded-2xl shadow-2xl p-4 relative border border-gray-200 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL}/company-heads/profilePicture/${selectedCompanyHead._id}`}
                    alt="Profile"
                    className="w-12 h-12 rounded-xl object-cover border-2 border-blue-200 shadow-sm"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-sm shadow-sm hidden">
                    {selectedCompanyHead.username?.charAt(0)?.toUpperCase()}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {selectedCompanyHead.username}
                  </h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                      {selectedCompanyHead.company?.name || "No Company Assigned"}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={closeDetailsModal}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-all duration-200"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Contact Information */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-xl border border-blue-200">
                <div className="flex items-center space-x-1 mb-2">
                  <FaEnvelope className="w-3 h-3 text-blue-600" />
                  <h3 className="text-sm font-bold text-gray-800">Contact Information</h3>
                </div>
                <div className="space-y-2">
                  <div className="bg-white p-2 rounded-lg">
                    <div className="flex items-center space-x-1 mb-1">
                      <FaEnvelope className="w-3 h-3 text-blue-600" />
                      <span className="font-semibold text-gray-700 text-xs">Email:</span>
                    </div>
                    <p className="text-gray-900 text-xs ml-4">{selectedCompanyHead.email}</p>
                  </div>
                  <div className="bg-white p-2 rounded-lg">
                    <div className="flex items-center space-x-1 mb-1">
                      <FaPhone className="w-3 h-3 text-blue-600" />
                      <span className="font-semibold text-gray-700 text-xs">Phone:</span>
                    </div>
                    <p className="text-gray-900 text-xs ml-4">{selectedCompanyHead.phoneNumber}</p>
                  </div>
                  <div className="bg-white p-2 rounded-lg">
                    <div className="flex items-center space-x-1 mb-1">
                      <FaMapMarkerAlt className="w-3 h-3 text-blue-600" />
                      <span className="font-semibold text-gray-700 text-xs">Address:</span>
                    </div>
                    <p className="text-gray-900 text-xs ml-4">{selectedCompanyHead.address}</p>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-xl border border-purple-200">
                <div className="flex items-center space-x-1 mb-2">
                  <FaCalendar className="w-3 h-3 text-purple-600" />
                  <h3 className="text-sm font-bold text-gray-800">Personal Information</h3>
                </div>
                <div className="space-y-2">
                  <div className="bg-white p-2 rounded-lg">
                    <span className="font-semibold text-gray-700 text-xs">Date of Birth:</span>
                    <p className="text-gray-900 text-xs mt-1">
                      {selectedCompanyHead.dateOfBirth
                        ? new Date(selectedCompanyHead.dateOfBirth).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : "Not specified"}
                    </p>
                  </div>
                  <div className="bg-white p-2 rounded-lg">
                    <span className="font-semibold text-gray-700 text-xs">Date of Joining:</span>
                    <p className="text-gray-900 text-xs mt-1">
                      {selectedCompanyHead.dateOfJoining
                        ? new Date(selectedCompanyHead.dateOfJoining).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : "Not specified"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-xl border border-green-200">
                <div className="flex items-center space-x-1 mb-2">
                  <FaBuilding className="w-3 h-3 text-green-600" />
                  <h3 className="text-sm font-bold text-gray-800">Company Information</h3>
                </div>
                <div className="bg-white p-2 rounded-lg">
                  {selectedCompanyHead.company ? (
                    <div className="flex items-center space-x-2">
                      <div className="bg-green-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs">
                        {selectedCompanyHead.company.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">{selectedCompanyHead.company.name}</h4>
                        <p className="text-gray-600 text-xs">Company Head</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-2">
                      <FaBuilding className="w-6 h-6 text-gray-300 mx-auto mb-1" />
                      <span className="text-gray-500 italic text-xs">No company assigned</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyHeadManagement;
